import React, { useState, useEffect } from "react";
import { getAvatarStyle } from "../utils/avatarColors";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { useDropzone } from "react-dropzone";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import recognitionTypesService from "../services/recognitionTypesService";
import teamsService from "../services/teamsService";
import workvivoService from "../services/workvivoService";

// Helper to extract hex color from stored format (e.g., "from-[#a855f7]" -> "#a855f7")
const extractHexColor = (colorString) => {
  if (!colorString) return null;
  const match = colorString.match(/\[(.*?)\]/);
  return match ? match[1] : colorString;
};

const RecognitionForm = ({ onSubmit, onClose }) => {
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      points: 50
    }
  });
  const [uploadedImage, setUploadedImage] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recognitionTypes, setRecognitionTypes] = useState([]);
  const [selectedRecognitionType, setSelectedRecognitionType] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [messageHtml, setMessageHtml] = useState("");
  const [visibility, setVisibility] = useState("org");
  const [myTeams, setMyTeams] = useState([]);
  const [selectedVisibilityTeam, setSelectedVisibilityTeam] = useState(null);
  const [wvIntegration, setWvIntegration] = useState(null); // {enabled, default_space_external_id, sender_linked}
  const [postToWorkvivo, setPostToWorkvivo] = useState(false);
  const [wvAudienceMode, setWvAudienceMode] = useState("default"); // 'default' | 'global' | 'custom'
  const [wvPostableSpaces, setWvPostableSpaces] = useState([]);
  const [wvSelectedSpaces, setWvSelectedSpaces] = useState([]);
  const [wvSpacesLoading, setWvSpacesLoading] = useState(false);
  const [allowGlobalPosting, setAllowGlobalPosting] = useState(false);

  // Whether this org lets senders make a recognition's point amount public
  useEffect(() => {
    recognitionTypesService
      .getPointsSettings()
      .then((s) => setAllowGlobalPosting(!!s?.allow_global_point_posting))
      .catch(() => setAllowGlobalPosting(false));
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: "Write a recognition message..." }),
    ],
    content: "",
    editorProps: {
      attributes: {
        spellcheck: "true",
      },
    },
    onUpdate: ({ editor }) => {
      setMessageHtml(editor.getHTML());
    },
  });

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedImage(URL.createObjectURL(file));
      setValue("media", file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    maxFiles: 1,
  });

  // Fetch all users for the dropdown with pagination
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const limit = 100;
      let skip = 0;
      let allUsers = [];
      let totalCount = 0;

      // Fetch all pages
      do {
        const response = await fetch(`${apiUrl}/api/v1/users/colleagues?skip=${skip}&limit=${limit}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const usersData = await response.json();
          allUsers = [...allUsers, ...usersData.data];
          totalCount = usersData.count;
          skip += limit;
        } else {
          break;
        }
      } while (allUsers.length < totalCount);

      const userOptions = allUsers.map(user => ({
        value: user.id,
        label: user.full_name || user.name || user.email,
        email: user.email,
        avatar: user.avatar_url,
        jobTitle: user.job_title
      }));
      setUsers(userOptions);

      // Set default selected user (first non-admin user or just first user)
      const defaultUser = userOptions.find(u => !u.email.includes('admin')) || userOptions[0];
      if (defaultUser) {
        setSelectedUser(defaultUser);
        setValue("recognizedUser", defaultUser.value);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recognition types from API
  const fetchRecognitionTypes = async () => {
    try {
      const types = await recognitionTypesService.getMyRecognitionTypes();
      const typeOptions = types.map(type => ({
        value: type.id,
        label: type.name,
        name: type.name,
        emoji: type.emoji,
        description: type.description,
        colorFrom: extractHexColor(type.color_from),
        colorTo: extractHexColor(type.color_to),
      }));
      setRecognitionTypes(typeOptions);
      if (typeOptions.length > 0) {
        setSelectedRecognitionType(typeOptions[0]);
        setValue("recognition_type_id", typeOptions[0].value);
      }
    } catch (error) {
      console.error('Error fetching recognition types:', error);
    }
  };

  // Fetch user's teams with budget info
  const fetchTeams = async () => {
    try {
      const budgets = await teamsService.getMyTeamBudgets();
      const teamOptions = (budgets || []).map(t => ({
        value: t.team_id,
        label: `${t.team_name || t.team_id} (${Math.floor(t.available_amount || 0)} pts available)`,
        teamName: t.team_name || t.team_id,
        availableAmount: t.available_amount || 0,
        budgetId: t.budget_id,
      }));
      setTeams(teamOptions);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Fetch all teams the current user belongs to (for visibility scoping)
  const fetchMyTeams = async () => {
    try {
      const teams = await teamsService.getMyTeams();
      const options = (teams || []).map(t => ({
        value: t.id || t.team_id,
        label: t.name || t.team_name || (t.id || t.team_id),
      }));
      setMyTeams(options);
    } catch (error) {
      console.error('Error fetching my teams:', error);
    }
  };

  // When a team is selected, fetch its members as user options
  const handleTeamChange = async (selected) => {
    setSelectedTeam(selected);
    setValue("team_id", selected ? selected.value : null);
    setSelectedUser(null);
    setValue("recognizedUser", "");
    if (selected) {
      try {
        const members = await teamsService.getTeamMembers(selected.value);
        const memberOptions = (members || [])
          .filter(m => m.is_active !== false)
          .map(m => ({
            value: m.user_id,
            label: m.full_name || m.email || m.user_id,
            email: m.email,
            avatar: m.avatar_url,
            jobTitle: m.job_title,
          }));
        setTeamMembers(memberOptions);
      } catch (error) {
        console.error('Error fetching team members:', error);
        setTeamMembers([]);
      }
    } else {
      setTeamMembers([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRecognitionTypes();
    fetchTeams();
    fetchMyTeams();
    // Check whether the org has Workvivo integration enabled.
    workvivoService
      .getRecognitionIntegrationStatus()
      .then((status) => setWvIntegration(status))
      .catch(() => setWvIntegration({ enabled: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch postable spaces whenever the recipient changes (and Workvivo is on)
  useEffect(() => {
    if (!postToWorkvivo || !selectedUser?.value) {
      setWvPostableSpaces([]);
      return;
    }
    setWvSpacesLoading(true);
    workvivoService
      .getPostableSpaces(selectedUser.value)
      .then((spaces) => setWvPostableSpaces(spaces || []))
      .catch(() => setWvPostableSpaces([]))
      .finally(() => setWvSpacesLoading(false));
  }, [postToWorkvivo, selectedUser]);

  const handleFormSubmit = (formData) => {
    if (!selectedUser) {
      alert('Please select a user to recognize');
      return;
    }
    if (!messageHtml || messageHtml === "<p><br></p>") {
      alert('Please write a recognition message');
      return;
    }
    if (visibility === "team" && !selectedVisibilityTeam) {
      alert('Please select a team for visibility');
      return;
    }

    // Resolve Workvivo space ids from the audience mode
    let workvivoSpaceIds = null;
    if (postToWorkvivo) {
      if (wvAudienceMode === "global") {
        workvivoSpaceIds = []; // empty array → global kudo
      } else if (wvAudienceMode === "default") {
        workvivoSpaceIds = wvIntegration?.default_space_external_id
          ? [wvIntegration.default_space_external_id]
          : [];
      } else if (wvAudienceMode === "custom") {
        if (!wvSelectedSpaces || wvSelectedSpaces.length === 0) {
          alert('Please select at least one Workvivo space (or switch to Global).');
          return;
        }
        workvivoSpaceIds = wvSelectedSpaces.map((s) => s.value);
      }

      // Pre-flight: confirm if recipient is not on Workvivo
      const recipientHasNoSpaces = wvPostableSpaces.length === 0;
      if (
        recipientHasNoSpaces &&
        wvAudienceMode === "custom"
      ) {
        // Already blocked by selection guard above.
      }
    }

    const submitData = {
      ...formData,
      message: messageHtml,
      recognized_user_id: selectedUser.value,
      recognition_type_id: selectedRecognitionType?.value || null,
      team_id: selectedTeam?.value || null,
      visibility,
      visible_team_id: visibility === "team" ? selectedVisibilityTeam?.value : null,
      post_to_workvivo: postToWorkvivo,
      workvivo_space_external_ids: workvivoSpaceIds,
      date: new Date().toISOString()
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-lg relative">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
          ✖
        </button>

        <h2 className="text-2xl font-semibold mb-4 text-center">🏆 Create a Recognition</h2>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Team Selection */}
          <label className="block text-sm font-medium text-gray-700">Team (for points):</label>
          <Select
            options={teams}
            value={selectedTeam}
            onChange={handleTeamChange}
            className="mt-1"
            isClearable
            placeholder="Select a team (optional, for points)..."
          />
          {selectedTeam && (
            <p className="text-xs text-gray-500 mt-1">
              Points will require team admin approval.
            </p>
          )}

          {/* Recognized User */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Recognizing:</label>
          {loading ? (
            <div className="mt-1 p-2 w-full border rounded bg-gray-100 flex items-center">
              <div className="animate-spin w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full mr-2"></div>
              Loading users...
            </div>
          ) : (
            <Select
              options={selectedTeam ? teamMembers : users}
              value={selectedUser}
              onChange={(selected) => {
                setSelectedUser(selected);
                setValue("recognizedUser", selected ? selected.value : "");
              }}
              className="mt-1"
              placeholder={selectedTeam ? "Select a team member..." : "Select a user to recognize..."}
              formatOptionLabel={(user) => (
                <div className="flex items-center">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.label}
                      className="w-8 h-8 rounded-full mr-3 object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full mr-3 flex items-center justify-center text-white font-bold text-xs" style={getAvatarStyle(user.label || user.email || user.value)}>
                      {user.label.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-medium">{user.label}</div>
                    <div className="text-xs text-gray-500">{user.jobTitle || user.email}</div>
                  </div>
                </div>
              )}
            />
          )}

          {/* Recognition Type */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Recognition Type:</label>
          <Select
            options={recognitionTypes}
            value={selectedRecognitionType}
            onChange={(selected) => {
              setSelectedRecognitionType(selected);
              setValue("recognition_type_id", selected ? selected.value : null);
            }}
            className="mt-1"
            placeholder="Select a recognition type..."
            formatOptionLabel={(type) => (
              <div className="flex items-center">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-white text-sm mr-2"
                  style={{
                    background: type.colorFrom && type.colorTo
                      ? `linear-gradient(to right, ${type.colorFrom}, ${type.colorTo})`
                      : 'linear-gradient(to right, #a855f7, #6366f1)',
                  }}
                >
                  {type.emoji && type.emoji.startsWith('/api/v1/recognition-type-icons/') ? (
                    <img src={type.emoji} alt="" className="w-5 h-5 object-contain" />
                  ) : (
                    type.emoji || '🏅'
                  )}
                </span>
                <span className="font-medium">{type.name}</span>
              </div>
            )}
          />

          {/* Selected type description */}
          <div
            className={`grid transition-all duration-300 ease-out ${
              selectedRecognitionType?.description
                ? "grid-rows-[1fr] opacity-100 mt-1"
                : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <p className="text-sm text-gray-500">{selectedRecognitionType?.description}</p>
            </div>
          </div>

          {/* Points */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Points:</label>
          <input
            {...register("points", { valueAsNumber: true })}
            type="number"
            min="1"
            max="100"
            className="mt-1 p-2 w-full border rounded"
            placeholder="50"
          />
          {allowGlobalPosting && Number(watch("points")) > 0 && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                {...register("make_points_public")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-gray-700">
                Make points public
              </span>
            </label>
          )}

          {/* Recognition Message */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Message:</label>
          <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-200 bg-gray-50">
              <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()}
                className={`px-2 py-1 rounded text-sm font-bold ${editor?.isActive("bold") ? "bg-gray-200" : "hover:bg-gray-100"}`}>B</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={`px-2 py-1 rounded text-sm italic ${editor?.isActive("italic") ? "bg-gray-200" : "hover:bg-gray-100"}`}>I</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleUnderline().run()}
                className={`px-2 py-1 rounded text-sm underline ${editor?.isActive("underline") ? "bg-gray-200" : "hover:bg-gray-100"}`}>U</button>
              <span className="w-px h-4 bg-gray-300 mx-1" />
              <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={`px-2 py-1 rounded text-sm ${editor?.isActive("bulletList") ? "bg-gray-200" : "hover:bg-gray-100"}`}>• List</button>
              <button type="button" onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={`px-2 py-1 rounded text-sm ${editor?.isActive("orderedList") ? "bg-gray-200" : "hover:bg-gray-100"}`}>1. List</button>
            </div>
            {/* Editor area */}
            <EditorContent
              editor={editor}
              className="min-h-[96px] px-3 py-2 text-sm focus-within:outline-none"
            />
          </div>

          {/* Visibility */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Who can see this?</label>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => { setVisibility("org"); setSelectedVisibilityTeam(null); }}
              className={`flex-1 px-3 py-2 rounded border text-sm ${visibility === "org" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
            >
              🌐 Everyone
            </button>
            <button
              type="button"
              onClick={() => setVisibility("team")}
              className={`flex-1 px-3 py-2 rounded border text-sm ${visibility === "team" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
            >
              👥 Team
            </button>
            <button
              type="button"
              onClick={() => { setVisibility("private"); setSelectedVisibilityTeam(null); }}
              className={`flex-1 px-3 py-2 rounded border text-sm ${visibility === "private" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
            >
              🔒 Private
            </button>
          </div>
          {visibility === "team" && (
            <>
              <Select
                options={myTeams}
                value={selectedVisibilityTeam}
                onChange={(selected) => setSelectedVisibilityTeam(selected)}
                className="mt-2"
                placeholder="Select a team that can see this..."
                noOptionsMessage={() => "You're not on any teams"}
              />
              <p className="text-xs text-gray-500 mt-1">
                Both you and the recipient must be members of the selected team.
              </p>
            </>
          )}
          {visibility === "private" && (
            <p className="text-xs text-gray-500 mt-1">
              Only you and the recipient will see this recognition.
            </p>
          )}

          {/* Workvivo cross-post */}
          {wvIntegration?.enabled && (
            <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={postToWorkvivo}
                  onChange={(e) => setPostToWorkvivo(e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium text-gray-700">
                  Also post as a kudo on Workvivo
                </span>
              </label>
              {postToWorkvivo && !wvIntegration.sender_linked && (
                <p className="text-xs text-orange-700 mt-2">
                  Heads up: your account is not linked to Workvivo, so posting may fail. You can still create the recognition.
                </p>
              )}
              {postToWorkvivo && (
                <div className="mt-3 space-y-2">
                  <label className="block text-xs font-medium text-gray-700">Audience</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setWvAudienceMode("default")}
                      disabled={!wvIntegration.default_space_external_id}
                      className={`flex-1 px-2 py-1.5 rounded border text-xs ${wvAudienceMode === "default" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"} disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={!wvIntegration.default_space_external_id ? "No default space configured" : ""}
                    >
                      ⭐ Default space
                    </button>
                    <button
                      type="button"
                      onClick={() => setWvAudienceMode("global")}
                      className={`flex-1 px-2 py-1.5 rounded border text-xs ${wvAudienceMode === "global" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}`}
                    >
                      🌐 Global (no space)
                    </button>
                    <button
                      type="button"
                      onClick={() => setWvAudienceMode("custom")}
                      className={`flex-1 px-2 py-1.5 rounded border text-xs ${wvAudienceMode === "custom" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}`}
                    >
                      🎯 Pick spaces
                    </button>
                  </div>
                  {wvAudienceMode === "custom" && (
                    <>
                      <Select
                        isMulti
                        isLoading={wvSpacesLoading}
                        options={wvPostableSpaces.map((s) => ({
                          value: s.external_space_id,
                          label: s.is_default ? `${s.name} (default)` : s.name,
                        }))}
                        value={wvSelectedSpaces}
                        onChange={(opts) => setWvSelectedSpaces(opts || [])}
                        placeholder={
                          !selectedUser
                            ? "Pick a recipient first..."
                            : wvPostableSpaces.length === 0
                              ? "No spaces — you and the recipient share none"
                              : "Select one or more spaces..."
                        }
                        noOptionsMessage={() => "No spaces — you and the recipient share none"}
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Only spaces where both you and the recipient are members are listed.
                      </p>
                    </>
                  )}
                  {visibility !== "org" && (
                    <p className="text-xs text-orange-700">
                      Heads up: this recognition is {visibility}-only in the app, but the Workvivo post will be visible to whoever can see the chosen audience.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* File Upload */}
          <label className="block text-sm font-medium text-gray-700 mt-3">Attach an Image (Optional):</label>
          <div {...getRootProps()} className="mt-2 border-dashed border-2 p-4 text-center cursor-pointer rounded bg-gray-100">
            <input {...getInputProps()} />
            {uploadedImage ? (
              <img src={uploadedImage} alt="Preview" className="w-full h-32 object-cover rounded mt-2" />
            ) : (
              <p className="text-gray-500">Drag & drop an image or click to upload</p>
            )}
          </div>

          {/* Submit Button */}
          <button type="submit" className="mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 w-full">
            Submit Recognition
          </button>
        </form>
      </div>
    </div>
  );
};

export default RecognitionForm;
